import { FunctionComponent, useState, useEffect } from 'react';
import styles from "./ChangePassword.module.css";
import cn from 'classnames';
import { Card } from 'react-bootstrap';
import CustomButton, { ButtonRadiusSize, ButtonType } from '../../../../../component/common/CustomButton/CustomButton';
import FormField, { IFieldTheme, IFieldType } from '../../../../../component/common/FormField/FormField';
import PreviewField from '../../../../../component/common/FormField/PreviewField';
import { useDispatch, useSelector } from 'react-redux';
import { resetPassword } from '../../../../../redux/slices/auth/actions';
import { AppDispatch, RootState } from '../../../../../redux/store';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import { changePasswordSchema } from '../../../../../validation/authValidation';
import { clearError, clearMessage, logoutSuccess } from '../../../../../redux/slices/auth/slice';
import { PageRoutes } from '../../../../../routing/paths';
import { useNavigate } from 'react-router';

interface IProps {
    className?: string;
};


const ChangePassword: FunctionComponent<IProps> = ({className}) => {

    const [editMode, setEditMode] = useState<boolean>(false);
    const dispatch = useDispatch<AppDispatch>();
    const { loading, error, message } = useSelector((state: RootState) => state.auth);
    const navigate= useNavigate();
    const formik = useFormik({
        initialValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
        validationSchema: changePasswordSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            const resultAction = await dispatch(resetPassword({ currentPassword: values.currentPassword, newPassword: values.newPassword }));
            if (resetPassword.fulfilled.match(resultAction)) {
                resetForm();
                setEditMode(false);
                dispatch(logoutSuccess());
                navigate(PageRoutes.LOGIN);
            }
            setSubmitting(false);
        },
    });

    useEffect(() => {
        if (loading === 'succeeded' && message) {
            toast.success(message);
            dispatch(clearMessage());
        }
        if (loading === 'failed' && error) {
            toast.error(error);
            dispatch(clearError());
        }
    }, [loading, message, error, dispatch]);

    const renderField = (label: string, name: string, placeholder: string) => {
        return (
            <div className={styles.formCol}>
                <FormField 
                    label={label}
                    fieldType={IFieldType.PASSWORD}
                    fieldTheme={IFieldTheme.LIGHT}
                    showEyeIcon= {true}
                    name={name}
                    placeholder={placeholder}
                    value={formik.values[name as keyof typeof formik.values]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    showError={formik.touched[name as keyof typeof formik.values] && !!formik.errors[name as keyof typeof formik.values]}
                    errorText={formik.errors[name as keyof typeof formik.values] as string}
                />
            </div>
        )
    }

    return (
        <Card bsPrefix={cn(styles.accountsettings, className, editMode && styles.editMode)}>
            <Card.Header bsPrefix={cn(styles.header)}>
                <div className={styles.userDetailWrap}>
                    <Card.Header bsPrefix={styles.headerTitle} as={'div'}>Security Settings</Card.Header>
                    {!editMode && 
                        <PreviewField 
                            title='Password' 
                            text={'********'}
                        />
                    }
                </div>
                {!editMode &&
                <div className={styles.options}>
                    <CustomButton
                        className={styles.edituserBtn}
                        btnType={ButtonType.GREEN}
                        onClick={() => {setEditMode(true)}}
                        btnRadius={ButtonRadiusSize.SMALL}
                    >
                        Change Password
                    </CustomButton>
                </div>
                }
            </Card.Header>
            {editMode &&
                <Card.Body bsPrefix={styles.body}>
                    <div className={styles.formWrap}>
                        {renderField('Current Password', 'currentPassword', 'Enter your current password')}
                        {renderField('New Password', 'newPassword', 'Enter new password')}
                        {renderField('Confirm New Password', 'confirmPassword', 'Enter new password again')}
                    </div>
                    {editMode && 
                        <div className={styles.options}>
                            <CustomButton
                                className={cn(styles.edituserBtn, styles.saveBtn)}
                                btnType={ButtonType.GREEN}
                                onClick={() => formik.handleSubmit()}
                                btnRadius={ButtonRadiusSize.SMALL}
                                disabled={formik.isSubmitting || !formik.isValid}
                            >
                                Update Password
                            </CustomButton>
                            <CustomButton
                                className={cn(styles.edituserBtn, styles.cancelBtn)}
                                btnType={ButtonType.RED}
                                onClick={() => {
                                    setEditMode(false);
                                    formik.resetForm();
                                }}
                                btnRadius={ButtonRadiusSize.SMALL}
                            >
                                Cancel
                            </CustomButton>
                        </div>
                    }
                </Card.Body>
            }
        </Card>
    );
};

export default ChangePassword;